import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AppSidebar from "./AppSidebar.js";
import AppTopBar from "./AppTopBar.js";
import UnitPanel from "./UnitPanel.js";
import { PageHeaderProvider } from "./PageHeaderContext.js";
import navSections from "layouts/navSections.js";
import useTheme from "theme/useTheme.js";
import auth from "services/auth.js";

const MOBILE_QUERY = "(max-width:899.95px)";

/**
 * Shell autenticado do Fatura — sidebar lateral fixa (desktop) + topbar.
 * No mobile (≤899.95px) a sidebar vira drawer. Componentes portados do
 * shell ErpOnline consumindo as peças de layout da @lukos-tecnologia/ui.
 */
export default function AdminShell({ children }) {
  useTheme(); // aplica data-theme (default light) na raiz
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [unitPanelOpen, setUnitPanelOpen] = useState(false);

  const [unidades, setUnidades] = useState([]);
  const [activeUnit, setActiveUnit] = useState("—");
  const [unitsLoading, setUnitsLoading] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUnitPanelOpen(false);
  }, [location.pathname]);

  const carregarUnidades = useCallback(async () => {
    const chave = localStorage.getItem("lukos-token-fw");
    try {
      const [lista, sel] = await Promise.all([
        auth.post("/faturaweb/uniddisppost", { params: { chave } }),
        auth.post("/faturaweb/unidselpost", { params: { chave } }),
      ]);
      if (lista.status === 200) setUnidades(lista.data || []);
      if (sel.status === 200) setActiveUnit(sel.data?.unidOpe || "—");
    } catch (e) {
      console.error("Erro ao carregar unidades:", e);
    } finally {
      setUnitsLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarUnidades();
  }, [carregarUnidades]);

  const selecionarUnidade = useCallback(async (idVinculo) => {
    const chave = localStorage.getItem("lukos-token-fw");
    try {
      const r = await auth.post(`/faturaweb/setaunid/'${idVinculo}`, {
        params: { chave },
      });
      if (r.status === 200) {
        localStorage.setItem("lukos-token-fw", r.data.token);
        window.location.href = "/";
      }
    } catch (e) {
      console.error("Erro ao trocar unidade:", e);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("lukos-token-fw");
    localStorage.removeItem("acesso");
    window.location.href = "/auth/login-page";
  }, []);

  const handleMenuToggle = () => {
    if (isMobile) setMobileOpen((v) => !v);
    else setCollapsed((v) => !v);
  };

  const sidebar = (
    <AppSidebar
      sections={navSections}
      loading={false}
      collapsed={!isMobile && collapsed}
      onToggleCollapsed={() =>
        isMobile ? setMobileOpen(false) : setCollapsed((v) => !v)
      }
      unitName={activeUnit}
    />
  );

  return (
    <div
      className={`app-shell${!isMobile && collapsed ? " app-shell--collapsed" : ""}`}
    >
      {/* Sidebar fixa (desktop) */}
      {!isMobile && sidebar}

      {/* Sidebar drawer (mobile) */}
      {isMobile && mobileOpen && (
        <>
          <div
            className="fatura-shell-backdrop"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <div className="fatura-shell-drawer">{sidebar}</div>
        </>
      )}

      <PageHeaderProvider>
        <div className="app-main">
          <AppTopBar
            sections={navSections}
            unitName={activeUnit}
            unitPanelOpen={unitPanelOpen}
            onUnitClick={() => setUnitPanelOpen((v) => !v)}
            onMenuToggle={handleMenuToggle}
          />

          <UnitPanel
            open={unitPanelOpen}
            unidades={unidades}
            loading={unitsLoading}
            activeUnitName={activeUnit}
            onClose={() => setUnitPanelOpen(false)}
            onSelectUnidade={selecionarUnidade}
            onLogout={handleLogout}
          />

          <main className="app-workspace">{children}</main>
        </div>
      </PageHeaderProvider>
    </div>
  );
}
